import { useEffect } from 'react';

/**
 * Imperatively upsert <meta>, <link rel="canonical"> and JSON-LD <script>
 * tags into document.head. Bypasses react-helmet-async 1.3.0 which is known
 * to drop meta/link children while still applying <title>.
 *
 * Usage:
 *   usePageMeta({
 *     title: 'Article title',
 *     description: 'Excerpt',
 *     canonical: 'https://e-mina.eu/clanak/slug',
 *     og: { type: 'article', title, description, image, url },
 *     twitter: { card: 'summary_large_image', image, title, description },
 *     jsonLd: { '@context': 'https://schema.org', ... },
 *   });
 *
 * On unmount we do NOT revert — the next page that uses this hook will
 * overwrite the same tags (we key everything by its name/property/rel).
 */
export default function usePageMeta({ title, description, canonical, og = {}, twitter = {}, article = {}, jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (keyAttr, keyValue, content) => {
      if (content == null || content === '') return;
      const selector = `meta[${keyAttr}="${keyValue}"]`;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(keyAttr, keyValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', String(content));
      el.setAttribute('data-page-meta', '1');
    };

    if (description) upsertMeta('name', 'description', description);

    // Canonical link
    if (canonical) {
      let linkEl = document.head.querySelector('link[rel="canonical"]');
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonical);
      linkEl.setAttribute('data-page-meta', '1');
    }

    Object.entries(og || {}).forEach(([k, v]) => upsertMeta('property', `og:${k}`, v));
    Object.entries(twitter || {}).forEach(([k, v]) => upsertMeta('name', `twitter:${k}`, v));
    Object.entries(article || {}).forEach(([k, v]) => upsertMeta('property', `article:${k}`, v));

    // JSON-LD: replace or insert our script
    const scriptId = 'page-jsonld';
    let scriptEl = document.getElementById(scriptId);
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.type = 'application/ld+json';
        scriptEl.id = scriptId;
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }
  }, [
    title, description, canonical,
    // Stringify objects so reference changes don't over-trigger
    JSON.stringify(og),
    JSON.stringify(twitter),
    JSON.stringify(article),
    JSON.stringify(jsonLd),
  ]);
}
